/**
 * Voice Payment Page
 *
 * Wrapper page for VOICE interaction type.
 * Renders AgentProgressUIVoiceDTMF component with session data.
 */

import { useCallback } from 'react';
import { AgentProgressUIVoiceDTMF } from '@components/AgentProgressUIVoiceDTMF';
import { MockDevPanel } from '@components/MockDevPanel';
import { APP_CONFIG } from '@utils/constants';
import { appendQueryParams } from '@utils/appendQueryParams';
import type { Session } from '@/types/session.types';
import type { OrderInfo, MockDTMFScenario } from '@/types/voice-dtmf.types';
import { logger } from '@utils/logger';

interface VoicePaymentPageProps {
  session: Session;
  /** Pre-validated payment session forwarded to useAgentApi → useAuthentication */
  initialSession?: PaymentSession;
}

/**
 * Detect mock scenario from sessionId
 *
 * Checks sessionId for keywords to determine scenario:
 * - no-saved → 'new-card-success-no-saved-cards' (no cards, direct to entry)
 * - saved-card → 'saved-card-success' (has saved cards)
 * - decline → 'new-card-decline'
 * - max-attempts → 'max-attempts-reached'
 * - partner-mode → 'partner-mode' (starts secure mode OFF, transitions to ON)
 * - default → 'new-card-success' (has saved cards by default)
 */
function detectMockScenario(sessionId: string): MockDTMFScenario {
  const id = sessionId.toLowerCase();

  // Check for delete card scenario
  if (id.includes('delete-card') || id.includes('deletecard')) {
    return 'delete-card-flow';
  }

  // Check for card expiry states test
  if (id.includes('card-expiry') || id.includes('expiry-states')) {
    return 'card-expiry-states';
  }

  // Check for "no saved" or "no-saved" pattern
  if (
    id.includes('no-saved') ||
    id.includes('nosaved') ||
    id.includes('no_saved')
  ) {
    return 'new-card-success-no-saved-cards';
  }

  // Check for saved card scenario
  if (id.includes('saved-card') || id.includes('savedcard')) {
    return 'saved-card-success';
  }

  // Check for decline scenario
  if (id.includes('decline')) {
    return 'payment-declined';
  }

  // Check for card expired scenario
  if (id.includes('card-expired') || id.includes('cardexpired')) {
    return 'card-expired';
  }

  // Check for card mismatch scenario
  if (id.includes('card-mismatch') || id.includes('cardmismatch')) {
    return 'card-mismatch';
  }

  // Check for DTMF capture error scenario
  if (id.includes('dtmf-capture') || id.includes('dtmf-error')) {
    return 'dtmf-capture-error';
  }

  // Check for partner mode scenarios
  if (id.includes('partner-mode-demo') || id.includes('partnermodedemo')) {
    return 'partner-mode-demo';
  }
  if (id.includes('partner-mode') || id.includes('partnermode')) {
    return 'partner-mode';
  }

  // Check for BFF error scenario
  if (id.includes('bff-error') || id.includes('bfferror')) {
    return 'bff-error';
  }

  // Check for max attempts scenario
  if (id.includes('max-attempts') || id.includes('maxattempts')) {
    return 'max-attempts';
  }

  // Check for error-retry scenario - map to payment-declined (shows error screen)
  if (id.includes('error-retry')) {
    return 'payment-declined';
  }

  // Check for error-declined scenario - map to payment-declined (shows error screen)
  if (id.includes('error-declined')) {
    return 'payment-declined';
  }

  // Check for timeout/expire scenario - map to payment-declined (shows error screen)
  if (id.includes('timeout') || id.includes('expire')) {
    return 'payment-declined';
  }

  // Default: new card with saved cards available
  return 'new-card-success';
}

/**
 * Voice Payment Page
 *
 * VOICE interaction type - displays DTMF collection UI
 */
export default function VoicePaymentPage({
  session,
  initialSession,
}: VoicePaymentPageProps) {
  const mockScenario = APP_CONFIG.USE_MOCK_DATA
    ? detectMockScenario(session.sessionId)
    : undefined;

  /**
   * Handle session completion
   */
  const handleSessionComplete = useCallback(
    (orderInfo: OrderInfo) => {
      logger.info('Voice payment completed', {
        sessionId: session.sessionId,
        orderInfo,
      });
      const returnUrl = initialSession?.agentReturnUrl;
      if (returnUrl) {
        const fullReturnUrl = appendQueryParams(returnUrl, {
          paymentId: initialSession?.paymentId,
        });
        logger.info('Redirecting agent to return URL after VOICE completion', {
          sessionId: session.sessionId,
        });
        window.location.href = fullReturnUrl;
      } else {
        window.parent.postMessage(
          {
            eventName: 'payment-outcome',
            type: 'payment.completed',
            payload: {
              paymentId: initialSession?.paymentId,
              status: 'COMPLETED',
              timestamp: new Date().toISOString(),
            },
          },
          '*'
        );
      }
    },
    [
      initialSession?.agentReturnUrl,
      initialSession?.paymentId,
      session.sessionId,
    ]
  );

  /**
   * Handle session declined (payment declined or max attempts exceeded)
   */
  const handleSessionDeclined = useCallback(
    (_pid?: string) => {
      logger.info('Voice payment declined', { sessionId: session.sessionId });
      const returnUrl = initialSession?.agentReturnUrl;
      if (returnUrl) {
        window.location.href = appendQueryParams(returnUrl, {
          paymentId: initialSession?.paymentId,
        });
      } else {
        window.parent.postMessage(
          {
            eventName: 'integration-failure',
            type: 'payment.completed',
            payload: {
              paymentId: initialSession?.paymentId,
              status: 'DECLINED',
              timestamp: new Date().toISOString(),
            },
          },
          '*'
        );
      }
    },
    [
      initialSession?.agentReturnUrl,
      initialSession?.paymentId,
      session.sessionId,
    ]
  );

  /**
   * Handle session cancellation
   */
  const handleSessionCancel = useCallback(() => {
    logger.info('Voice payment cancelled', { sessionId: session.sessionId });
    const returnUrl = initialSession?.agentReturnUrl;
    if (returnUrl) {
      window.location.href = appendQueryParams(returnUrl, {
        paymentId: initialSession?.paymentId,
      });
    } else {
      window.parent.postMessage(
        {
          eventName: 'integration-failure',
          type: 'payment.completed',
          payload: {
            paymentId: initialSession?.paymentId,
            status: 'CANCELLED',
            timestamp: new Date().toISOString(),
          },
        },
        '*'
      );
    }
  }, [
    initialSession?.agentReturnUrl,
    initialSession?.paymentId,
    session.sessionId,
  ]);

  /**
   * Handle fallback to manual mode
   */
  const handleFallbackToManual = useCallback((sessionId: string) => {
    logger.info('Fallback to manual mode triggered', { sessionId });
    // Reload page to fetch updated session (backend changed interactionType to VOICE_MANUAL)
    window.location.reload();
  }, []);

  /**
   * Handle error
   */
  const handleError = useCallback(
    (error: Error) => {
      logger.error('Voice payment error', error, {
        sessionId: session.sessionId,
      });
      // TODO: Show error message
    },
    [session.sessionId]
  );

  const shouldSaveCard: 'ALWAYS' | 'OPTIONAL' | 'NEVER' =
    APP_CONFIG.USE_MOCK_DATA
      ? (() => {
          const sid = session.sessionId.toLowerCase();
          if (sid.includes('always-save') || sid.includes('alwayssave'))
            return 'ALWAYS';
          if (sid.includes('save-card') || sid.includes('savecard'))
            return 'OPTIONAL';
          return 'NEVER';
        })()
      : (initialSession?.shouldSaveCard ?? 'NEVER');

  return (
    <>
      <AgentProgressUIVoiceDTMF
        sessionId={session.sessionId}
        customerId={session.customerId}
        amount={session.amount}
        currency={session.currency}
        useMockData={APP_CONFIG.USE_MOCK_DATA}
        mockScenario={mockScenario}
        mockAutoProgress={false}
        onSessionComplete={handleSessionComplete}
        onSessionDeclined={handleSessionDeclined}
        onSessionCancel={handleSessionCancel}
        onFallbackToManual={handleFallbackToManual}
        onError={handleError}
        initialSession={initialSession}
        initialManualModeEnabled={initialSession?.manualModeEnabled}
        shouldSaveCard={shouldSaveCard}
        callReference={initialSession?.callReference}
        partnerModeEnabled={initialSession?.partnerModeEnabled}
        interactionType="VOICE"
        paymentId={initialSession?.paymentId}
        cancelButtonEnabled={initialSession?.cancelButtonEnabled ?? false}
      />
      {APP_CONFIG.USE_MOCK_DATA && <MockDevPanel />}
    </>
  );
}
